
import React, { useState, useEffect } from 'react';
import { X, Save, AlertCircle } from 'lucide-react';

const AdminUserModal = ({ isOpen, onClose, user, onSave, isLoading }) => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        role: 'admin'
    });
    const [errors, setErrors] = useState({});

    useEffect(() => {
        if (user) {
            setFormData({
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                password: '', // Password not shown
                role: user.role
            });
        } else {
            setFormData({
                firstName: '',
                lastName: '',
                email: '',
                password: '',
                role: 'admin'
            });
        }
        setErrors({});
    }, [user, isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    const validate = () => {
        const newErrors = {};
        if (!formData.firstName.trim()) newErrors.firstName = 'Ad zorunludur';
        if (!formData.lastName.trim()) newErrors.lastName = 'Soyad zorunludur';
        if (!formData.email.trim()) newErrors.email = 'E-posta zorunludur';
        // Password is required only for new users
        if (!user && !formData.password) newErrors.password = 'Şifre zorunludur';
        if (formData.password && formData.password.length < 6) newErrors.password = 'Şifre en az 6 karakter olmalıdır';

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validate()) {
            onSave(formData);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-secondary-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-100 bg-gray-50/50">
                    <h2 className="text-xl font-bold text-gray-900">
                        {user ? 'Yöneticiyi Düzenle' : 'Yeni Yönetici Ekle'}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Ad <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="firstName"
                                value={formData.firstName}
                                onChange={handleChange}
                                className={`input ${errors.firstName ? 'border-red-300 focus:ring-red-200' : ''}`}
                                placeholder="Ad"
                            />
                            {errors.firstName && <p className="text-xs text-red-500 mt-1">{errors.firstName}</p>}
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Soyad <span className="text-red-500">*</span></label>
                            <input
                                type="text"
                                name="lastName"
                                value={formData.lastName}
                                onChange={handleChange}
                                className={`input ${errors.lastName ? 'border-red-300 focus:ring-red-200' : ''}`}
                                placeholder="Soyad"
                            />
                            {errors.lastName && <p className="text-xs text-red-500 mt-1">{errors.lastName}</p>}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">E-posta <span className="text-red-500">*</span></label>
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            className={`input ${errors.email ? 'border-red-300 focus:ring-red-200' : ''}`}
                            placeholder="ornek@stokly.com"
                        />
                        {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            {user ? 'Yeni Şifre (İsteğe bağlı)' : 'Şifre'} <span className={user ? 'hidden' : 'text-red-500'}>*</span>
                        </label>
                        <input
                            type="password"
                            name="password"
                            value={formData.password}
                            onChange={handleChange}
                            className={`input ${errors.password ? 'border-red-300 focus:ring-red-200' : ''}`}
                            placeholder={user ? "Değiştirmek için girin" : "••••••"}
                        />
                        {errors.password && <p className="text-xs text-red-500 mt-1">{errors.password}</p>}
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Rol</label>
                        <select
                            name="role"
                            value={formData.role}
                            onChange={handleChange}
                            className="input"
                        >
                            <option value="admin">Admin (Standart)</option>
                            <option value="super_admin">Süper Admin (Tam Yetki)</option>
                        </select>
                    </div>

                    {/* Guidelines */}
                    <div className="bg-secondary-50 p-3 rounded-lg flex items-start gap-2 text-xs text-secondary-600">
                        <AlertCircle size={14} className="mt-0.5 shrink-0" />
                        <p>
                            Süper Adminler tüm sistem ayarlarına erişebilir.
                            Adminler sadece müşteri ve özellik yönetimi yapabilir.
                        </p>
                    </div>

                    <div className="flex items-center justify-end gap-3 mt-6">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={isLoading}
                            className="btn-primary px-4 py-2 flex items-center gap-2"
                        >
                            {isLoading ? (
                                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            ) : (
                                <Save size={16} />
                            )}
                            {user ? 'Güncelle' : 'Oluştur'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default AdminUserModal;
